import { useCallback, useEffect, useState } from "react";
import { replace } from "lodash";
import { useHistory } from "react-router-dom";
import { LoadingProgress, TableFooter } from "@/ui";
import type { AlgoliaOrder } from "@/types";
import routes from "config/routes/routes";
import { useOrdersData } from "./context/OrdersDataContext";
import KanbanModal from "./kanban/KanbanModal";
import { OrderTable } from "./OrderTable";
import styles from "./OrderTableList.module.scss";

/** Same options as Accounts / Treez / StoreUsers TableFooter UIs. */
const PAGE_SIZE_OPTIONS = [10, 25, 50, 75, 100];

const OrderTableList = () => {
    const { orders, loading, fetchMore, totalCount, pageSize, setPageSize } =
        useOrdersData();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [algoliaOrder, setAlgoliaOrder] = useState<AlgoliaOrder>();
    const [pageIndex, setPageIndex] = useState(0);
    const [targetIndex, setTargetIndex] = useState<number | null>(null);
    const [lastOrdersLength, setLastOrdersLength] = useState(0);

    const history = useHistory();

    const handleModalToggle = useCallback((order: AlgoliaOrder) => {
        setIsModalOpen(prev => !prev);
        setAlgoliaOrder(order);
    }, []);

    const handleDetailOpen = useCallback(
        (id: string) => {
            const url = replace(routes.ORDER_DETAIL.route, ":id", id);
            history.push(url);
        },
        [history],
    );

    const total = totalCount ?? 0;
    const pageCount = Math.max(1, Math.ceil(total / pageSize) || 1);

    const handlePageChange = useCallback(
        (page1Based: number) => {
            const nextIndex = page1Based - 1;
            if (nextIndex < 0 || nextIndex >= pageCount) return;

            const pageStart = nextIndex * pageSize;
            if (pageStart < (orders?.length || 0)) {
                setPageIndex(nextIndex);
                return;
            }

            /* Need more data from the server before showing this page. */
            setTargetIndex(nextIndex);
            setLastOrdersLength(orders?.length || 0);
            fetchMore();
        },
        [fetchMore, orders?.length, pageCount, pageSize],
    );

    const handlePageSizeChange = useCallback(
        (size: number) => {
            if (!PAGE_SIZE_OPTIONS.includes(size) || size === pageSize) return;
            setPageSize(size);
            setPageIndex(0);
            setTargetIndex(null);
            setLastOrdersLength(0);
        },
        [pageSize, setPageSize],
    );

    useEffect(() => {
        if (targetIndex === null) return;

        const pageStart = targetIndex * pageSize;
        const loaded = orders?.length || 0;

        if (loaded > pageStart) {
            setPageIndex(targetIndex);
            setTargetIndex(null);
            return;
        }

        if (loaded >= total) {
            setPageIndex(Math.min(targetIndex, Math.max(0, pageCount - 1)));
            setTargetIndex(null);
            return;
        }

        if (loaded > lastOrdersLength) {
            setLastOrdersLength(loaded);
            fetchMore();
        }
    }, [
        orders,
        lastOrdersLength,
        targetIndex,
        total,
        pageCount,
        pageSize,
        fetchMore,
    ]);

    /* Reset pagination when filters change the result total. */
    useEffect(() => {
        setPageIndex(0);
        setTargetIndex(null);
        setLastOrdersLength(orders?.length || 0);
    }, [totalCount]);

    /* Reset page index when page size changes (refetch comes from context). */
    useEffect(() => {
        setPageIndex(0);
        setTargetIndex(null);
    }, [pageSize]);

    const start = pageIndex * pageSize;
    const end = start + pageSize;
    const visibleOrders = (orders || []).slice(start, end);
    /* Same pattern as Accounts / Customers: full-table LoadingProgress on query
       load/refetch — keep table visible while fetchMore fills a target page. */
    const showTableLoading = loading && targetIndex === null;

    return (
        <div className={styles.root}>
            {showTableLoading ? (
                <div className={styles.loading}>
                    <LoadingProgress
                        indeterminate
                        indicator="circular"
                        size="sm"
                        title="Loading"
                    />
                </div>
            ) : (
                <>
                    <OrderTable
                        handleDetailOpen={handleDetailOpen}
                        handleModalToggle={handleModalToggle}
                        orders={visibleOrders}
                    />
                    <TableFooter
                        className={styles.footer}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        page={pageIndex + 1}
                        pageSize={pageSize}
                        pageSizeOptions={PAGE_SIZE_OPTIONS}
                        totalPages={pageCount}
                        totalRows={total}
                    />
                </>
            )}
            {algoliaOrder ? (
                <KanbanModal
                    algoliaOrder={algoliaOrder}
                    handleModalToggle={handleModalToggle}
                    isModalOpen={isModalOpen}
                />
            ) : null}
        </div>
    );
};

export default OrderTableList;
